// navigation.helper.ts
import { GetFormSection } from "../services/api/formSections-service";
import type { IFormSection } from "../services/model/formSection";


export const DrawBackgroundTypeEnum = {
    None: 0,
    VehicleDamage: 1,
    Signature: 2
} as const;

export type DrawBackgroundTypeEnum =
    (typeof DrawBackgroundTypeEnum)[keyof typeof DrawBackgroundTypeEnum];

export interface INavigationData {
    name: string;
    id: string; // formId (unique per step)
    index: number;
    formType: string;
    formSectionId: string; // container/section
    backgroundType?: number;
    description: string;
    required: boolean;
}

/* -------------------- utils -------------------- */
const LS_KEYS = {
    navigation: "navigation",
    currentScreenIndex: "currentScreenIndex",
    formId: "formId",
    formTypeId: "formTypeId",
} as const;

function tryParse<T = unknown>(s: string | null, fallback: T): T {
    try {
        return s ? (JSON.parse(s) as T) : fallback;
    } catch {
        return fallback;
    }
}

function setIndex(i: number) {
    localStorage.setItem(LS_KEYS.currentScreenIndex, String(i));
}

function getIndex(): number {
    const raw = Number(localStorage.getItem(LS_KEYS.currentScreenIndex) ?? "0");
    return Number.isFinite(raw) && raw >= 0 ? raw : 0;
}

function getNav(): INavigationData[] {
    return tryParse<INavigationData[]>(
        localStorage.getItem(LS_KEYS.navigation),
        []
    );
}

function setNav(list: INavigationData[]) {
    localStorage.setItem(LS_KEYS.navigation, JSON.stringify(list));
}

/* -------------------- bootstrapping -------------------- */
/**
 * Fetch sections and materialize navigation list.
 * IMPORTANT: we DO NOT blindly reset the step to 0 anymore.
 * We preserve the current step if it's still valid for the new nav list.
 */
export const getNavigation = async (authTokens: string) => {
    const formId = tryParse<string>(localStorage.getItem(LS_KEYS.formId), "");
    if (!formId) {
        console.warn("getNavigation: missing formId in localStorage");
        return;
    }

    const response = await GetFormSection(authTokens, formId);
    if (!response) {
        console.log(
            "There seems to be a problem with your internet connection.\nPlease check your internet connection."
        );
        return;
    }

    const sections = (response.data as IFormSection[]) ?? [];
    if (!sections.length) {
        console.log("There are no sections available for your profile.");
        setNav([]);
        setIndex(0);
        return;
    }

    const nav: INavigationData[] = sections
        .filter(Boolean)
        .map((fs) => ({
            name: String(fs.title),
            id: String(fs.formId),
            index: Number(fs.index),
            formType: String(fs.formType),
            formSectionId: String(fs.formSectionId),
            backgroundType: typeof fs.backgroundType === "number" ? (fs.backgroundType as DrawBackgroundTypeEnum) : DrawBackgroundTypeEnum.None,
            description: String(fs.description),
            required: Boolean(fs.required)
        }))
        .filter((x) => !!x.id)
        .sort((a, b) => a.index - b.index);

    const prevIndex = getIndex();
    setNav(nav);

    // Preserve the previous index if it's still valid; otherwise clamp.
    const max = Math.max(0, nav.length - 1);
    const clamped = Math.max(0, Math.min(prevIndex, max));
    setIndex(clamped);
};

/* -------------------- inspectors -------------------- */
export function GetCurrentForm(): INavigationData | null {
    const nav = getNav();
    if (!nav.length) return null;

    const idx = getIndex();
    return nav.find((x) => x.index === idx) ?? null;
}

export function GetNavigationLength(): number {
    return getNav().length;
}

/* -------------------- movers (bounded) -------------------- */
function navLen(): number {
    return getNav().length;
}

export const SetNextLocation = () => {
    const idx = getIndex();
    const len = navLen();

    if (len > 0) {
        const max = Math.max(0, len - 1);
        setIndex(Math.min(idx + 1, max));
        return;
    }

    // nav not loaded yet; still move forward sensibly
    setIndex(idx + 1);
};

export const SetPreviouslocation = () => {
    const idx = getIndex();
    // previous is always bounded at 0 even without nav
    setIndex(Math.max(idx - 1, 0));
};

export const SetLocation = (i: number) => {
    const safe = Number.isFinite(i) ? Math.max(0, i) : 0;
    const len = navLen();

    if (len > 0) {
        const max = Math.max(0, len - 1);
        setIndex(Math.min(safe, max));
        return;
    }

    // nav not loaded yet; don't destroy the URL step by clamping to 0
    setIndex(safe);
};


export function GetIndexFromLS(): number {
    return getIndex();
}

/* -------------------- router target -------------------- */
/**
 * Build the path AND include ?step=<index>&node=<formId>.
 * Uses the same index accessor everywhere so step stays consistent.
 */
export function NavigateTo(options?: { cancel?: boolean; step?: number }): string {
    const nav = getNav();
    const idx = Number.isFinite(options?.step)
        ? Math.max(0, options!.step!)
        : getIndex();

    if (options?.cancel || !nav.length) {
        return `/accidentSupportList?step=${idx}`;
    }

    const record =
        nav.find((x) => x.index === idx) ??
        nav[Math.max(0, Math.min(idx, nav.length - 1))];

    if (!record) return `/accidentSupportList?step=${idx}`;

    localStorage.setItem(LS_KEYS.formTypeId, JSON.stringify(record.id));

    let next = "/accidentSupportList";
    switch (record.formType) {
        case "Question":
            next = "/dynamicform";
            break;
        case "QuestionList":
            next = "/dynamiclist";
            break;
        case "Camera":
            next = "/uploads";
            break;
        case "Draw":
            next = "/accidentSupportDraw";
            break;
        case "Final Screen":
            next = "/accidentSupportFinal";
            break;
    }

    return `${next}?step=${idx}&node=${encodeURIComponent(record.id)}`;
}


/* -------------------- simple helpers (return URL only) -------------------- */
/**
 * These helpers only update the stored index and return the URL to navigate to.
 * Let your React components call `navigate(url)` (or your tenant-aware `go()`).
 * This keeps React Router in sync and avoids the "jump to step 0" issue.
 */
export const BuildNextUrl = (): string => {
    SetNextLocation();
    return NavigateTo();
};

export const BuildPrevUrl = (): string => {
    SetPreviouslocation();
    return NavigateTo();
};

export const BuildGotoUrl = (i: number): string => {
    SetLocation(i);
    return NavigateTo();
};

export const BuildBackUrl = (step?: number): string => {
    const idx = Number.isFinite(step) ? Math.max(0, step!) : getIndex();

    if (idx <= 0) {
        return NavigateTo({ cancel: true, step: 0 });
    }

    const prev = idx - 1;
    SetLocation(prev);               // keep LS aligned
    return NavigateTo({ step: prev });// explicit route
};


export async function EnsureNavigationLoaded(authTokens: string): Promise<boolean> {
    // already loaded?
    if (GetNavigationLength() > 0) return true;

    try {
        await getNavigation(authTokens);
        return GetNavigationLength() > 0;
    } catch (e) {
        console.error("EnsureNavigationLoaded failed", e);
        return false;
    }
}


